import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Tabs,
  Badge,
  Button,
  Skeleton,
  Alert,
  ConfirmDialog,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { locationService } from '@/services/location.service';
import { formatPaiseCompact } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { LocationDetailTab } from '@/components/features/locations/LocationDetailTab';
import { LocationStaffTab } from '@/components/features/locations/LocationStaffTab';
import { LocationSettingsTab } from '@/components/features/locations/LocationSettingsTab';
import { LocationInventoryTab } from '@/components/features/locations/LocationInventoryTab';
import { LocationPerformanceTab } from '@/components/features/locations/LocationPerformanceTab';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

type KpiTone = 'success' | 'warning' | 'danger' | 'info' | 'brand';

function toneClasses(tone: KpiTone) {
  switch (tone) {
    case 'success':
      return { icon: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', accent: 'from-emerald-500/20 to-emerald-500/0' };
    case 'warning':
      return { icon: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', accent: 'from-amber-500/18 to-amber-500/0' };
    case 'danger':
      return { icon: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400', accent: 'from-rose-500/18 to-rose-500/0' };
    case 'info':
      return { icon: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400', accent: 'from-sky-500/18 to-sky-500/0' };
    default:
      return { icon: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400', accent: 'from-indigo-500/18 to-indigo-500/0' };
  }
}

// ─── KPI card ───────────────────────────────────────────────────────────────
function KpiStatCard({
  label,
  value,
  tone,
  icon,
  onClick,
}: {
  label: string;
  value: string | number;
  tone: KpiTone;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  const palette = toneClasses(tone);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        GLASS_PANEL,
        'relative overflow-hidden p-5 text-left transition-all duration-200 hover:shadow-lg w-full',
        onClick && 'cursor-pointer',
      )}
    >
      <div className={cn('absolute inset-x-0 top-0 h-14 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
            {value}
          </p>
        </div>
      </div>
    </button>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────────────
function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}
function PackageIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}
function CurrencyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function ChartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}
const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'staff', label: 'Staff' },
  { id: 'settings', label: 'Settings' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'performance', label: 'Performance' },
];

// ─── Content ────────────────────────────────────────────────────────────────

function LocationDetailContent() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currency = useTenantCurrency();

  const activeTab = searchParams.get('tab') ?? 'details';
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const {
    data: location,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['location', id],
    queryFn: () => locationService.getById(id!),
    enabled: Boolean(id),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => locationService.update(id!, { is_active: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', id] });
      setConfirmDeactivate(false);
      toast({ title: 'Location deactivated', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => locationService.update(id!, { is_active: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', id] });
      toast({ title: 'Location reactivated', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  function setTab(tabId: string) {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabId);
    setSearchParams(params);
  }

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in" role="status" aria-label="Loading location details">
        <Skeleton variant="rect" height={80} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={140} />
          ))}
        </div>
        <Skeleton variant="rect" height={40} />
        <Skeleton variant="rect" height={300} />
      </div>
    );
  }

  if (error || !location) {
    return (
      <Alert
        variant="danger"
        title="Failed to load location"
        description="The location may not exist or you lack permission to view it."
        action={{ label: 'Back to Locations', onClick: () => navigate('/locations') }}
      />
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Hero Header — glass panel with location info + actions */}
      <div className={cn(GLASS_PANEL, 'p-5 mb-6')}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-500 text-white font-bold text-lg">
              {location.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                {location.name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="info">{location.location_code}</Badge>
                <Badge variant={location.is_active ? 'success' : 'danger'}>
                  {location.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {location.is_active ? (
              <Button variant="danger" size="sm" onClick={() => setConfirmDeactivate(true)}>
                Deactivate
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                loading={reactivateMutation.isPending}
                onClick={() => reactivateMutation.mutate()}
              >
                Reactivate
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* KPI Row — spec: staff count, products, today's sales, stock value */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiStatCard
          label="Staff Count"
          value={location.user_count ?? 0}
          tone="brand"
          icon={<UsersIcon />}
          onClick={() => setTab('staff')}
        />
        <KpiStatCard
          label="Products"
          value={location.product_count ?? 0}
          tone="info"
          icon={<PackageIcon />}
          onClick={() => setTab('inventory')}
        />
        <KpiStatCard
          label="Today's Sales"
          value={formatPaiseCompact(location.today_sales_paise ?? 0, currency)}
          tone="success"
          icon={<CurrencyIcon />}
        />
        <KpiStatCard
          label="Stock Value"
          value={formatPaiseCompact(location.stock_value_paise ?? 0, currency)}
          tone="warning"
          icon={<ChartIcon />}
        />
      </div>

      {/* Tabs */}
      <Tabs
        items={TABS}
        activeId={activeTab}
        onChange={setTab}
        scrollable
      />

      <div className="mt-6">
        {activeTab === 'details' && <LocationDetailTab location={location} />}
        {activeTab === 'staff' && <LocationStaffTab locationId={id!} />}
        {activeTab === 'settings' && <LocationSettingsTab location={location} />}
        {activeTab === 'inventory' && <LocationInventoryTab locationId={id!} />}
        {activeTab === 'performance' && <LocationPerformanceTab locationId={id!} />}
      </div>

      {/* Deactivate Confirm */}
      {confirmDeactivate && (
        <ConfirmDialog
          open
          title="Deactivate Location"
          description={`Deactivating "${location.name}" will prevent billing at this location. Staff will be unassigned. Continue?`}
          confirmLabel="Deactivate"
          variant="danger"
          loading={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate()}
          onCancel={() => setConfirmDeactivate(false)}
        />
      )}
    </div>
  );
}

// ─── Page wrapper ───────────────────────────────────────────────────────────

export function LocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const cachedLocation = queryClient.getQueryData<{ name: string }>(['location', id]);
  const displayName = cachedLocation?.name ?? 'Location';

  return (
    <PageContainer
      title={displayName}
      breadcrumbs={[{ label: 'Locations', href: '/locations' }, { label: displayName }]}
    >
      <ErrorBoundary resetKey={id}>
        <LocationDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
